'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import dynamic from 'next/dynamic';

// Utils
import getConfig from '@config';
import { zIndex } from '@utils/constants';

// UI
import palette from '@color-palette';
import { Typography } from '@components/typography';
import { Map, MapMarker, MapCircle, MapMarkerDefaultIcon } from '@components/map';
import { MapProvider } from '@components/map/map-context';
import { formatListingAddress } from '@utils/format-listing-address';
type Props = {
  lat: number;
  lng: number;
  addressLineOne?: string;
  city?: string;
  postalCode?: string;
  stateOrProvinceLongName?: string;
  locationObfuscated: boolean;
};

/**
 * This module relies on the class google.maps.OverlayView which
 * is only available on the window once the google maps script has loaded.
 */
const MapOverlay = dynamic(() => import('@app/(search)/_components/search/search-map/map-overlay'), {
  ssr: false
});
export const MapPreview: React.FC<Props> = t0 => {
  const $ = _c(35);
  const {
    addressLineOne,
    city,
    postalCode,
    stateOrProvinceLongName,
    lat,
    lng,
    locationObfuscated
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getConfig();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    publicRuntimeConfig: t2
  } = t1;
  const {
    google: t3
  } = t2;
  const {
    apiKey
  } = t3;
  const t4 = locationObfuscated ? "" : addressLineOne;
  let t5;
  if ($[1] !== city || $[2] !== postalCode || $[3] !== stateOrProvinceLongName || $[4] !== t4) {
    t5 = formatListingAddress({
      addressLineOne: t4,
      city,
      postalCode,
      stateOrProvinceLongName
    });
    $[1] = city;
    $[2] = postalCode;
    $[3] = stateOrProvinceLongName;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const address = t5;
  const [infoCardOpen, setInfoCardOpen] = React.useState(false);
  let t6;
  if ($[6] !== address) {
    t6 = <Typography component="h4" variantM="title4" variant="subtitle2" className="font-medium !text-lg" data-testid="@map/listing-address-name">{address}</Typography>;
    $[6] = address;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== locationObfuscated) {
    t7 = locationObfuscated && <Typography component="p" variant="small" className="my-3 text-gray-main" data-testid="@map/listing-address-location">Location provided after the reservation is made.</Typography>;
    $[8] = locationObfuscated;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      flexGrow: "1",
      height: "100%",
      borderRadius: "6px"
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== lat || $[12] !== lng) {
    t9 = {
      lat,
      lng
    };
    $[11] = lat;
    $[12] = lng;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== lat || $[15] !== lng || $[16] !== locationObfuscated) {
    t10 = locationObfuscated && <MapCircle center={{
      lat,
      lng
    }} radius={304.8} fillColor={palette.primary.main} strokeColor={palette.primary.light} strokeOpacity={0.4} strokePosition={2} fillOpacity={0.8} strokeWeight={100} clickable={true} onClick={() => {
      setInfoCardOpen(true);
    }} />;
    $[14] = lat;
    $[15] = lng;
    $[16] = locationObfuscated;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== lat || $[19] !== lng || $[20] !== locationObfuscated) {
    t11 = !locationObfuscated && <MapMarker position={{
      lat,
      lng
    }} cursor="pointer" icon={MapMarkerDefaultIcon} />;
    $[18] = lat;
    $[19] = lng;
    $[20] = locationObfuscated;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== infoCardOpen || $[23] !== lat || $[24] !== lng) {
    t12 = infoCardOpen && lat && lng && <MapOverlay position={{
      lat: lat + 0.01,
      lng: lng + 0.0125
    }}><div style={{
        backgroundColor: palette.gray.main,
        borderRadius: "2px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
        color: palette.white,
        minWidth: 250,
        textAlign: "center",
        padding: 8,
        zIndex: zIndex.stickyBar
      }}>You will receive the exact location after booking</div></MapOverlay>;
    $[22] = infoCardOpen;
    $[23] = lat;
    $[24] = lng;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t12 || $[29] !== t9) {
    t13 = <div className="h-[350px]" data-testid="@map/map-area"><Wrapper apiKey={apiKey}><MapProvider><Map style={t8} center={t9} zoom={11} clickableIcons={true}>{t10}{t11}{t12}</Map></MapProvider></Wrapper></div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t6 || $[33] !== t7) {
    t14 = <div>{t6}{t7}{t13}</div>;
    $[31] = t13;
    $[32] = t6;
    $[33] = t7;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
};